import { Archive, ArchiveRestore } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmPopover } from '~/components/common/confirm-popover';
import { Button, type ButtonProps } from '~/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

interface ArchiveToggleButtonProps {
  /** Current state — picks Archive vs Unarchive. */
  archived: boolean;
  isPending: boolean;
  /** Archive call — resolves/rejects when the request settles so the confirm
   *  popover can close itself either way (the mutation owns the toasts and any
   *  follow-up dialog for a refused, still-executing run). */
  onArchive: () => Promise<unknown>;
  onUnarchive: () => void;
  /** Button look: rows use a muted ghost icon; detail headers an outline icon. */
  buttonVariant?: ButtonProps['variant'];
  buttonSize?: ButtonProps['size'];
  /** Also print the label after the icon (detail headers ≥1680px, like their
   *  sibling actions). Rows stay icon-only. */
  showLabel?: boolean;
  className?: string;
  /** `data-testid` for the toggle; its confirm popover gets `${testId}-confirm-popover`. */
  testId?: string;
}

/**
 * Single control for both directions of the visibility flag. Archive asks for a
 * light inline confirmation (non-destructive styling — nothing is stopped or
 * deleted); Unarchive is instant because it is a safe, exact inverse. The button
 * is never pre-disabled on display status: the server probes the run and
 * archives a row that reads live but is provably dead, and a genuine refusal
 * comes back as a 409 the owning hook turns into a Stop / Reconcile dialog.
 * Clicks never bubble to the (clickable) table row.
 */
export function ArchiveToggleButton({
  archived,
  isPending,
  onArchive,
  onUnarchive,
  buttonVariant = 'ghost',
  buttonSize = 'icon',
  showLabel = false,
  className,
  testId = 'archive-toggle-button',
}: ArchiveToggleButtonProps) {
  const { t } = useTranslation('common');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const label = archived ? t('listArchive.action.unarchive') : t('listArchive.action.archive');
  const Icon = archived ? ArchiveRestore : Archive;

  // The Button forwards its ref, so it can serve directly as the popover
  // trigger; the popover's own onClick is composed onto it by Radix.
  const button = (
    <Button
      type="button"
      variant={buttonVariant}
      size={buttonSize}
      aria-label={label}
      data-testid={testId}
      isLoading={isPending}
      className={cn('cursor-pointer disabled:cursor-not-allowed', className)}
      onClick={(e) => {
        // The whole row navigates on click — the toggle must not.
        e.stopPropagation();
        if (archived) onUnarchive();
      }}
    >
      {!isPending && <Icon className="h-4 w-4" aria-hidden="true" />}
      {showLabel && (
        <span className={cn('max-[1679px]:hidden', 'min-[1680px]:inline')}>{label}</span>
      )}
    </Button>
  );

  const control = archived ? (
    button
  ) : (
    <ConfirmPopover
      open={confirmOpen}
      onOpenChange={setConfirmOpen}
      title={t('listArchive.confirm.title')}
      description={t('listArchive.confirm.body')}
      confirmLabel={t('listArchive.confirm.confirm')}
      cancelLabel={t('listArchive.confirm.cancel')}
      confirmVariant="default"
      isPending={isPending}
      onConfirm={() => {
        // Close on success and on failure alike — the row either left the list,
        // a toast explained why it didn't, or a Stop dialog took over.
        void onArchive().then(
          () => {
            setConfirmOpen(false);
          },
          () => {
            setConfirmOpen(false);
          },
        );
      }}
      align="end"
      testId={`${testId}-confirm-popover`}
      trigger={button}
    />
  );

  // Tooltip sits outside the popover so its trigger is a real element (the
  // span), not a Radix root.
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <span className="inline-flex">{control}</span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
